/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-08-23 14:49:19
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import { SubmissionError } from 'redux-form';
import { store } from 'src/stores/store';
import { FAKED_PRODUCT_COUNT } from 'src/constants/filter-tree';

class CollectionHelper {
  /**
   * Init values from first loading
   *
   * @param  {object} filterOption  filter option value
   * @param  {object} initialValue  default values
   *
   * @return {object}               init values
   */
  static initialValues = (filterOption: Object, initialValue: Object) => {
    const {
      displayType,
      activeCollectionAll,
      manualValues,
      keepValuesStatic,
      valueType,
      excludedValues
    } = filterOption;

    // Define parse function
    const funcParseTag = tagItem => {
      const { tag, displayName, subTags = [] } = tagItem;
      if (subTags.length > 0) {
        return {
          tag: { value: tag, label: tag },
          displayName: displayName,
          subTags: subTags.map(subTag => funcParseTag(subTag))
        };
      } else {
        return {
          tag: { value: tag, label: tag },
          displayName: displayName
        };
      }
    };

    let subCategory = [];
    let collectionManualValues = [];
    let collectionExcludedValues = [];
    let collectionMultiLevelManualValues = [];

    if (displayType === 'sub_category') {
      subCategory = manualValues.map(item => {
        let result = {};
        result.collection = { value: item.id, label: item.label, handle: item.handle };
        result.tags = item.tags ? item.tags.map(i => ({ value: i, label: i })) : [];
        return result;
      });
    } else if (displayType === 'list') {
      if (valueType === 'specific') {
        collectionManualValues = manualValues
          ? manualValues.map(item => ({
              value: item.id,
              label: item.label,
              handle: item.handle,
              sort_order: item.sort_order
            }))
          : [];
      } else {
        collectionExcludedValues = excludedValues
          ? excludedValues.map(item => ({
              value: item.id,
              label: item.label,
              handle: item.handle,
              sort_order: item.sort_order
            }))
          : [];
      }
    } else {
      collectionMultiLevelManualValues = manualValues.map(item => ({
        value: item.id,
        label: item.label,
        handle: item.handle,
        sort_order: item.sort_order,
        displayName: item.displayName,
        tags: item.tags ? item.tags.map(tag => funcParseTag(tag)) : []
      }));
    }

    const result = {
      ...initialValue,
      collectionDisplayType: displayType,
      collectionOptionValues: valueType,
      collectionManualValues: collectionManualValues,
      collectionExcludedValues: collectionExcludedValues,
      collectionSubCategory: _.isEmpty(subCategory) ? [{}] : subCategory,
      collectionMultiLevelManualValues,
      collectionActiveCollectionAll: activeCollectionAll,
      collectionKeepValuesStatic: keepValuesStatic
    };

    return result;
  };

  /**
   * Build filter option data based on input values
   *
   * @param  {object} filterOption    input values
   * @param  {object} defaultValues   default values
   *
   * @return {object}                 filter option values
   */
  static buildFilterOptionValues = (filterOption: Object, defaultValues: Object) => {
    // Define parse function
    const funcParseTag = tagItem => {
      const { tag, displayName, subTags = [] } = tagItem;
      const value = _.get(tag, 'value', '');
      if (_.isEmpty(value)) return null;

      return subTags.length > 0
        ? {
            tag: _.get(tag, 'value', ''),
            displayName,
            subTags: subTags.map(subTag => funcParseTag(subTag)).filter(subTag => subTag)
          }
        : {
            tag: _.get(tag, 'value', ''),
            displayName
          };
    };

    const {
      collectionDisplayType,
      collectionOptionValues,
      collectionManualValues,
      collectionExcludedValues,
      collectionSubCategory,
      collectionMultiLevelManualValues
    } = filterOption;
    // Build Manual values
    let manualValues = null;
    let excludedValues = null;
    if (collectionDisplayType === 'list') {
      if (collectionOptionValues === 'specific') {
        manualValues = collectionManualValues
          ? collectionManualValues.map(item => {
              let result = {};
              if (item) {
                result.handle = item.handle;
                result.id = item.value;
                result.label = item.label;
                result.sort_order = item.sort_order;
              }
              return result;
            })
          : null;
      } else {
        excludedValues = collectionExcludedValues
          ? collectionExcludedValues.map(item => {
              let result = {};
              if (item) {
                result.handle = item.handle;
                result.id = item.value;
                result.label = item.label;
                result.sort_order = item.sort_order;
              }
              return result;
            })
          : null;
      }
    } else if (collectionDisplayType === 'sub_category') {
      manualValues = collectionSubCategory
        ? collectionSubCategory.map(item => {
            let result = {};
            const { collection } = item;
            if (collection) {
              result.handle = collection.handle;
              result.id = collection.value;
              result.label = collection.label;
              result.tags = item.tags ? item.tags.map(i => i.value) : [];
              result.sort_order = item.sort_order;
            }
            return result;
          })
        : null;
    } else {
      manualValues = collectionMultiLevelManualValues
        ? collectionMultiLevelManualValues
            .map(item => {
              let result = {};
              result.handle = item.handle;
              result.id = item.value;
              result.label = item.label;
              result.tags = item.tags
                ? item.tags.map(tag => funcParseTag(tag)).filter(tag => tag)
                : [];
              result.displayName = item.displayName;
              result.sort_order = item.sort_order;
              return result;
            })
            .filter(item => item.id)
        : null;
    }

    const { collectionKeepValuesStatic, collectionActiveCollectionAll } = filterOption;

    const result = {
      ...defaultValues,
      valueType: collectionOptionValues,
      selectType: 'single',
      displayType: collectionDisplayType,
      keepValuesStatic: collectionKeepValuesStatic,
      activeCollectionAll: collectionActiveCollectionAll,
      manualValues,
      excludedValues
    };

    return result;
  };

  /**
   * Validate for collection
   *
   * @param  {object} filterOption
   * @param  {I18n} i18n
   *
   * @return {boolean/error} void if well validated, throw error if have
   */
  static validateFilterOption = (filterOption: Object, i18n: Object) => {
    // Check manual value
    if (filterOption.collectionDisplayType === 'list') {
      if (
        filterOption.collectionOptionValues === 'specific' &&
        _.isEmpty(filterOption.collectionManualValues)
      ) {
        throw new SubmissionError({
          collectionManualValues: i18n.translate(
            'AdminHTML.FilterTree.Helper.collectionHelper_ValidateFilterOption_SubmissionError_AtLeast'
          )
        });
      }
    }

    if (filterOption.collectionDisplayType === 'sub_category') {
      if (_.isEmpty(filterOption.collectionSubCategory)) {
        throw new SubmissionError({
          collectionSubCategory: {
            _error: i18n.translate(
              'AdminHTML.FilterTree.Helper.collectionHelper_ValidateFilterOption_SubmissionError_AtLeast'
            )
          }
        });
      } else {
        let collectionError = [];
        filterOption.collectionSubCategory.map((item, index) => {
          if (_.isEmpty(item.collection)) {
            collectionError[index] = {
              collection: i18n.translate(
                'AdminHTML.FilterTree.Helper.collectionHelper_ValidateFilterOption_SubmissionError_SelectOne'
              )
            };
          }
          return '';
        });

        if (!_.isEmpty(collectionError)) {
          throw new SubmissionError({
            collectionSubCategory: collectionError
          });
        }
      }
    }

    if (filterOption.collectionDisplayType === 'multi_level_collections') {
      const { collectionMultiLevelManualValues } = filterOption;
      if (_.isEmpty(collectionMultiLevelManualValues)) {
        throw new SubmissionError({
          collectionMultiLevelManualValues: i18n.translate(
            'AdminHTML.FilterTree.Helper.collectionHelper_ValidateFilterOption_SubmissionError_AtLeast'
          )
        });
      }
    }
  };

  /**
   * Remove no props for comparison
   *
   * @param  {array} list list tag
   * @return {array}      updated list
   */
  static removeEmptyNoCompareProps = (list: Object) => {
    const cloned = _.cloneDeep(list);

    // Define parse function
    const funcParseTag = tagItem => {
      const { tag, displayName, subTags = [] } = tagItem;

      // Delete expanded
      delete tagItem.expanded;

      // Process sub tags
      if (subTags.length > 0)
        return { tag, displayName, subTags: subTags.map(subTag => funcParseTag(subTag)) };

      // Delete subTags if empty array
      delete tagItem.subTags;
      return tagItem;
    };

    return cloned.map(item => {
      const { value, label, handle, displayName, tags } = item;

      // Delete expanded
      delete item.expanded;

      if (tags.length > 0) {
        return { value, label, handle, displayName, tags: tags.map(tag => funcParseTag(tag)) };
      }

      // Delete empty tags
      delete item.tags;
      return item;
    });
  };

  /**
   * Fake product data for collection
   *
   * @param  {object} filterOption
   * @return {array}              list of product count
   */
  static fakeProductValues = (filterOption: Object) => {
    const { valueType } = filterOption;
    let values = [];

    if (valueType === 'specific') {
      let manualValues = _.get(filterOption, 'manualValues', []);

      // Limit product item
      manualValues = _.slice(manualValues, 0, FAKED_PRODUCT_COUNT);

      values = _.map(manualValues, collection => {
        const { id, handle, label, tags, sort_order } = collection;
        return {
          doc_count: Math.floor(Math.random() * 100) + 1,
          handle,
          key: id,
          label,
          tags: tags || null,
          sort_order
        };
      });
    } else {
      const currentState = store.getState();
      let items = _.cloneDeep(_.get(currentState.filterData, `filterData.collections`, []));
      const excludedValues = _.get(filterOption, 'excludedValues', []);

      // Remove excluded values
      if (!_.isEmpty(excludedValues)) {
        _.map(excludedValues, item => _.remove(items, i => i.value === item.id));
      }

      // Limit product item
      const subItems = _.slice(items, 0, FAKED_PRODUCT_COUNT);

      values = _.map(subItems, collection => {
        const { handle, label, value, sort_order } = collection;

        return {
          doc_count: Math.floor(Math.random() * 100) + 1,
          handle,
          key: value,
          label,
          tags: null,
          sort_order
        };
      });
    }
    return values;
  };

  /**
   * Check selected manual values is empty
   *
   * @param  {array} manualValues
   *
   * @return {boolean}              true | false
   */
  static isEmptyMultiLevelManualValues = (manualValues: Object) => {
    if (_.isEmpty(manualValues) || (_.isArray(manualValues) && manualValues.length === 0))
      return true;

    const collection = _.get(manualValues, '[0]', {});
    if (_.isArray(manualValues) && manualValues.length === 1 && _.isEmpty(collection.handle)) {
      return true;
    }

    return false;
  };

  /**
   * Remove selected tags
   *
   * @param  {array} initialTags list of all tags
   * @param  {array} manualValues
   * @param  {number} key number of current selected
   *
   * @return {array}              list of tags after removing selected tag
   */
  static removeSelectedTags = (initialTags: Object, manualValues: Object, key: number) => {
    const tags = _.cloneDeep(initialTags);

    const removeTag = tagItem => {
      const { tag, subTags = [] } = tagItem;

      // Remove tag
      _.remove(tags, { value: tag.value });
      subTags.forEach(subTagItem => {
        removeTag(subTagItem);
      });
    };

    const collection = manualValues[key];
    const { tags: selectedTags = [] } = collection;
    selectedTags.forEach(tagItem => {
      removeTag(tagItem);
    });

    return tags;
  };
}

export default CollectionHelper;
